/**
 * ParticleFx — render-only pooled particles + floating text + dash afterimages (juice Tier A/B).
 *
 * Lives OUTSIDE `src/engine` (unlinted) so it may use `Math.random`/`Math.sin`/`Math.cos`
 * freely. Purely PRESENTATION: everything here is emitted from deterministic sim events but
 * its own state (positions/velocities/lifetimes) is NEVER read by `World.update()`, so the
 * cosmetic randomness cannot affect replay parity (same firewall as fx/Juice). Sprites are
 * pooled (acquire/release) to avoid per-emit allocation.
 *
 * `layer` must be added to World.worldLayer so particles live in world-space (camera + shake
 * apply). Advance once per render via update(dt); drop everything on respawn via clear().
 */
import { Container, Sprite, Text, Texture } from 'pixi.js';

interface Particle {
  spr: Sprite; vx: number; vy: number; life: number; max: number; grav: number; drag: number; spin: number;
}
interface FloatText { txt: Text; vy: number; life: number; max: number; }
interface After { spr: Sprite; life: number; max: number; }

export class ParticleFx {
  /** Add to World.worldLayer (world-space). Holds particles, floating text, and afterimages. */
  readonly layer = new Container();
  /** When true, no new fx spawn (accessibility — mirrors Juice.reducedFx). */
  reduced = false;

  private live: Particle[] = [];
  private pool: Sprite[] = [];
  private texts: FloatText[] = [];
  private textPool: Text[] = [];
  private afters: After[] = [];
  private afterPool: Sprite[] = [];

  private take(): Sprite {
    const s = this.pool.pop() ?? new Sprite(Texture.WHITE);
    s.anchor.set(0.5);
    s.rotation = 0;
    s.visible = true;
    this.layer.addChild(s);
    return s;
  }
  private give(s: Sprite): void { this.layer.removeChild(s); s.visible = false; this.pool.push(s); }

  /** Feet dust: pale, low, outward + up, gentle gravity. `dir` biases horizontal spread. */
  dust(x: number, y: number, count: number, dir = 0): void {
    if (this.reduced) return;
    for (let i = 0; i < count; i++) {
      const s = this.take();
      s.tint = 0xdedede; s.alpha = 0.9;
      const sz = 3 + Math.random() * 4; s.width = sz; s.height = sz;
      s.position.set(x + (Math.random() - 0.5) * 12, y);
      this.live.push({
        // Wide lateral spread, low upward kick — dust fans OUT to the sides, not straight up.
        spr: s, vx: (Math.random() - 0.5) * 260 + dir * 55, vy: -22 - Math.random() * 60,
        life: 0, max: 0.24 + Math.random() * 0.2, grav: 720, drag: 2.6, spin: 0,
      });
    }
  }

  /** Coin sparkle: a few small gold bits popping outward + up. */
  sparkle(x: number, y: number, color = 0xffe66d): void {
    if (this.reduced) return;
    for (let i = 0; i < 5; i++) {
      const s = this.take();
      s.tint = color; s.alpha = 1;
      const sz = 3 + Math.random() * 3; s.width = sz; s.height = sz;
      s.position.set(x, y);
      const a = Math.random() * 6.2832; const sp = 80 + Math.random() * 130;
      this.live.push({ spr: s, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 60, life: 0, max: 0.3 + Math.random() * 0.2, grav: 320, drag: 3, spin: 0 });
    }
  }

  /** Radial burst: enemy-death pop. */
  burst(x: number, y: number, count = 9, color = 0xffffff): void {
    if (this.reduced) return;
    for (let i = 0; i < count; i++) {
      const s = this.take();
      s.tint = color; s.alpha = 1;
      const sz = 4 + Math.random() * 4; s.width = sz; s.height = sz;
      s.position.set(x, y);
      const a = (i / count) * 6.2832 + Math.random() * 0.6; const sp = 120 + Math.random() * 170;
      this.live.push({ spr: s, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0, max: 0.35 + Math.random() * 0.25, grav: 520, drag: 2, spin: (Math.random() - 0.5) * 12 });
    }
  }

  /** Floating "+100"-style text that rises + fades. */
  pop(x: number, y: number, text: string, color = 0xffffff): void {
    if (this.reduced) return;
    const t = this.textPool.pop() ?? new Text({
      text: '', style: { fontFamily: 'system-ui, sans-serif', fontSize: 20, fontWeight: '800', fill: '#fff', stroke: { color: '#1b2030', width: 4 } },
    });
    t.text = text; t.tint = color; t.alpha = 1; t.anchor.set(0.5); t.position.set(x, y); t.visible = true;
    this.layer.addChild(t);
    this.texts.push({ txt: t, vy: -62, life: 0, max: 0.7 });
  }

  /** A fading blue snapshot of the player during a dash (motion trail). */
  afterImage(tex: Texture, x: number, y: number, scaleX: number, scaleY: number): void {
    if (this.reduced) return;
    const s = this.afterPool.pop() ?? new Sprite(tex);
    s.texture = tex; s.anchor.set(0.5, 1); s.scale.set(scaleX, scaleY);
    s.position.set(x, y); s.tint = 0x66ccff; s.alpha = 0.5; s.visible = true;
    this.layer.addChild(s);
    this.afters.push({ spr: s, life: 0, max: 0.22 });
  }

  update(dt: number): void {
    const d = dt > 0.05 ? 0.05 : dt;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i];
      p.life += d;
      if (p.life >= p.max) { this.give(p.spr); this.live.splice(i, 1); continue; }
      p.vy += p.grav * d;
      p.vx *= 1 - Math.min(1, p.drag * d);
      p.spr.x += p.vx * d; p.spr.y += p.vy * d;
      if (p.spin) p.spr.rotation += p.spin * d;
      p.spr.alpha = 1 - p.life / p.max;
    }
    for (let i = this.texts.length - 1; i >= 0; i--) {
      const t = this.texts[i];
      t.life += d;
      if (t.life >= t.max) { this.layer.removeChild(t.txt); t.txt.visible = false; this.textPool.push(t.txt); this.texts.splice(i, 1); continue; }
      t.txt.y += t.vy * d; t.vy *= 1 - Math.min(1, 1.5 * d);
      t.txt.alpha = 1 - t.life / t.max;
    }
    for (let i = this.afters.length - 1; i >= 0; i--) {
      const a = this.afters[i];
      a.life += d;
      if (a.life >= a.max) { this.layer.removeChild(a.spr); a.spr.visible = false; this.afterPool.push(a.spr); this.afters.splice(i, 1); continue; }
      a.spr.alpha = 0.5 * (1 - a.life / a.max);
    }
  }

  /** Recycle everything (called on respawn so a death clears in-flight fx). */
  clear(): void {
    for (const p of this.live) this.give(p.spr);
    this.live.length = 0;
    for (const t of this.texts) { this.layer.removeChild(t.txt); t.txt.visible = false; this.textPool.push(t.txt); }
    this.texts.length = 0;
    for (const a of this.afters) { this.layer.removeChild(a.spr); a.spr.visible = false; this.afterPool.push(a.spr); }
    this.afters.length = 0;
  }
}
